import { useContext, useEffect, useRef, useState } from 'react'
import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Link from '@tiptap/extension-link'
import { Bold, Check, Italic, Link2, Quote, X } from 'lucide-react'
import { CitationNode } from './CitationNode'
import { SourceListContext } from './SourceListContext'
import { inlineToTiptap, tiptapToInline } from './converters'
import type { WireArticleBlock, WireInlineBlock } from '../../../api/types'

// ─── Toolbar button ───────────────────────────────────────────────────────────

function ToolbarBtn({
  onClick,
  active,
  title,
  children,
}: {
  onClick: () => void
  active?: boolean
  title: string
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      // onMouseDown keeps editor focus; click would blur then re-focus
      onMouseDown={(e) => { e.preventDefault(); onClick() }}
      title={title}
      className={`rounded p-1.5 transition-colors ${
        active
          ? 'bg-accent-primary-fill text-accent-primary-fg'
          : 'bg-fill text-fg-secondary hover:bg-fill-hover hover:text-fg'
      }`}
    >
      {children}
    </button>
  )
}

// Extensions defined at module level so Tiptap never sees duplicate registrations
// across re-renders or HMR updates.
const EXTENSIONS = [
  StarterKit.configure({
    // Disable all block-level nodes — this is an inline-only paragraph editor
    heading: false,
    blockquote: false,
    bulletList: false,
    orderedList: false,
    listItem: false,
    codeBlock: false,
    horizontalRule: false,
    hardBreak: false,
  }),
  Link.configure({ openOnClick: false }),
  CitationNode,
]

// ─── ParagraphBlockEditor ─────────────────────────────────────────────────────

interface Props {
  block: Extract<WireArticleBlock, { type: 'paragraph' }>
  onSave: (children: WireInlineBlock[]) => void
  onCancel: () => void
}

export function ParagraphBlockEditor({ block, onSave, onCancel }: Props) {
  const sources = useContext(SourceListContext)
  const [showCitations, setShowCitations] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  // Stable refs so event listeners never capture stale closures
  const onSaveRef = useRef(onSave)
  const onCancelRef = useRef(onCancel)
  useEffect(() => { onSaveRef.current = onSave }, [onSave])
  useEffect(() => { onCancelRef.current = onCancel }, [onCancel])

  // Click outside → cancel
  useEffect(() => {
    function handleMouseDown(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        onCancelRef.current()
      }
    }
    document.addEventListener('mousedown', handleMouseDown)
    return () => document.removeEventListener('mousedown', handleMouseDown)
  }, [])

  const editor = useEditor({
    extensions: EXTENSIONS,
    content: inlineToTiptap(block.children),
    editorProps: {
      attributes: {
        class: 'text-sm leading-relaxed text-fg outline-none',
      },
    },
    autofocus: 'end',
    immediatelyRender: false,
  })

  // Cmd/Ctrl+S → save; Escape → cancel (window-level to catch ProseMirror events)
  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault()
        if (editor) onSaveRef.current(tiptapToInline(editor.getJSON()))
      }
      if (e.key === 'Escape') {
        onCancelRef.current()
      }
    }
    window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
  }, [editor])

  function handleSave() {
    if (!editor) return
    onSave(tiptapToInline(editor.getJSON()))
  }

  function handleLink() {
    if (!editor) return
    if (editor.isActive('link')) {
      editor.chain().focus().unsetLink().run()
    } else {
      const url = window.prompt('URL:')
      if (url) editor.chain().focus().setLink({ href: url }).run()
    }
  }

  function insertCitation(sourceId: string) {
    editor?.chain().focus().insertContent({ type: 'citation', attrs: { sourceId } }).run()
    setShowCitations(false)
  }

  return (
    <div ref={containerRef} className="rounded ring-2 ring-accent-primary-solid">

      {/* Toolbar */}
      <div className="flex items-center gap-1 rounded-t border-b border-line bg-surface px-2.5 py-1.5">

        {/* Classification badge */}
        {block.classification && (
          <span className="mr-1.5 rounded bg-teal-3 px-1.5 py-0.5 font-mono text-[9px] font-bold text-teal-11">
            ({[block.classification, ...(block.disseminationControls ?? [])].join('//')})
          </span>
        )}

        <ToolbarBtn
          active={editor?.isActive('bold') ?? false}
          onClick={() => editor?.chain().focus().toggleBold().run()}
          title="Bold (⌘B)"
        >
          <Bold size={14} />
        </ToolbarBtn>

        <ToolbarBtn
          active={editor?.isActive('italic') ?? false}
          onClick={() => editor?.chain().focus().toggleItalic().run()}
          title="Italic (⌘I)"
        >
          <Italic size={14} />
        </ToolbarBtn>

        <ToolbarBtn
          active={editor?.isActive('link') ?? false}
          onClick={handleLink}
          title="Link"
        >
          <Link2 size={14} />
        </ToolbarBtn>

        {/* Citation picker */}
        <div className="relative">
          <ToolbarBtn
            active={showCitations}
            onClick={() => setShowCitations(v => !v)}
            title="Insert citation"
          >
            <Quote size={14} />
          </ToolbarBtn>
          {showCitations && (
            <div className="absolute left-0 top-full z-50 mt-1 min-w-[300px] rounded border border-line bg-surface shadow-lg">
              {sources.length === 0 ? (
                <p className="px-3 py-2 text-xs text-fg-secondary">No sources in this article</p>
              ) : (
                sources.map((src, i) => (
                  <button
                    key={src.id}
                    type="button"
                    onMouseDown={(e) => { e.preventDefault(); insertCitation(src.id) }}
                    className="flex w-full items-start gap-2 px-3 py-2 text-left text-xs hover:bg-fill"
                  >
                    <span className="shrink-0 font-semibold text-accent-primary-fg">[{i + 1}]</span>
                    <span className="line-clamp-2 text-fg-secondary">{src.text}</span>
                  </button>
                ))
              )}
            </div>
          )}
        </div>

        {/* Save / Cancel */}
        <div className="ml-auto flex items-center gap-1">
          <button
            type="button"
            onMouseDown={(e) => { e.preventDefault(); onCancel() }}
            className="flex items-center gap-1 rounded px-2.5 py-1.5 text-xs text-fg-secondary hover:bg-fill hover:text-fg"
            title="Discard changes (Esc)"
          >
            <X size={13} />
            Cancel
          </button>
          <button
            type="button"
            onMouseDown={(e) => { e.preventDefault(); handleSave() }}
            className="flex items-center gap-1 rounded bg-accent-primary-solid px-2.5 py-1.5 text-xs font-semibold text-white transition-opacity hover:opacity-90"
            title="Save changes (⌘S)"
          >
            <Check size={13} />
            Save
          </button>
        </div>
      </div>

      {/* Editor area */}
      <div className="px-4 py-2.5">
        <EditorContent editor={editor} />
      </div>

    </div>
  )
}
